/**
 * Storage Cleanup Utility
 *
 * Removes over-persisted data that violates privacy and creates UX issues.
 * Run this on app initialization to clean up user's browsers.
 */

import { DEPRECATED_KEYS, PERSISTENT_KEYS, SESSION_KEYS } from './storage-keys';

interface CleanupResult {
  removed: string[];
  errors: string[];
}

/**
 * PRIVACY FIX: Migrate delivery info from localStorage to sessionStorage
 *
 * Delivery information should NOT persist between browser sessions (GDPR):
 * - Migrate to sessionStorage (survives refresh, clears on tab close)
 * - Remove from localStorage (was persisting indefinitely)
 *
 * This provides better UX (survives refresh) while maintaining privacy (clears on close)
 */
export function removeSensitiveDeliveryInfo(): CleanupResult {
  const result: CleanupResult = { removed: [], errors: [] };

  if (typeof window === 'undefined') return result;

  try {
    // Check both old localStorage key and current sessionStorage key name
    const localStorageKey = PERSISTENT_KEYS.DELIVERY_INFO;
    const sessionStorageKey = SESSION_KEYS.DELIVERY_INFO;
    const localStorageData = window.localStorage.getItem(localStorageKey);

    // Migrate to sessionStorage if it exists in localStorage
    if (localStorageData) {
      try {
        // Only migrate if sessionStorage doesn't already have it
        if (!window.sessionStorage.getItem(sessionStorageKey)) {
          window.sessionStorage.setItem(sessionStorageKey, localStorageData);
        }
        // Remove from localStorage (privacy compliance)
        window.localStorage.removeItem(localStorageKey);
        result.removed.push(localStorageKey);
      } catch (migrationError) {
        // If migration fails, still remove from localStorage
        window.localStorage.removeItem(localStorageKey);
        result.removed.push(localStorageKey);
        const errorMsg =
          migrationError instanceof Error ? migrationError.message : String(migrationError);
        result.errors.push(`Migration failed but removed from localStorage: ${errorMsg}`);
      }
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    result.errors.push(`Failed to migrate/remove delivery info: ${errorMsg}`);
  }

  return result;
}

/**
 * Remove duplicate email storage keys
 *
 * Consolidates to single source of truth: innolek_user_email
 */
export function consolidateEmailStorage(): CleanupResult {
  const result: CleanupResult = { removed: [], errors: [] };

  if (typeof window === 'undefined') return result;

  try {
    // Get email from any old location
    let preservedEmail: string | null = null;

    // Check all old email keys
    for (const oldKey of DEPRECATED_KEYS.OLD_EMAIL_KEYS) {
      const email = window.localStorage.getItem(oldKey);
      if (email && !preservedEmail) {
        preservedEmail = email;
      }

      if (window.localStorage.getItem(oldKey)) {
        window.localStorage.removeItem(oldKey);
        result.removed.push(oldKey);
      }
    }

    // Migrate to new single key if we found an old email
    if (preservedEmail && !window.localStorage.getItem(PERSISTENT_KEYS.EMAIL)) {
      window.localStorage.setItem(PERSISTENT_KEYS.EMAIL, preservedEmail);
    }

    if (result.removed.length > 0) {
      /* Intentionally empty */
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    result.errors.push(`Failed to consolidate email storage: ${errorMsg}`);
  }

  return result;
}

/**
 * Move session-only flags from localStorage to sessionStorage
 *
 * These flags should NOT persist between browser sessions:
 * - hasShownEmailPrompt (user should see prompt again in new session)
 * - Business logic cached flags
 */
export function migrateSessionFlags(): CleanupResult {
  const result: CleanupResult = { removed: [], errors: [] };

  if (typeof window === 'undefined') return result;

  try {
    // Remove email prompt flag - should NOT persist
    if (window.localStorage.getItem(DEPRECATED_KEYS.EMAIL_PROMPT_FLAG)) {
      window.localStorage.removeItem(DEPRECATED_KEYS.EMAIL_PROMPT_FLAG);
      result.removed.push(DEPRECATED_KEYS.EMAIL_PROMPT_FLAG);
    }

    // Remove any other session-only flags that ended up in localStorage
    const sessionOnlyKeys = [
      'business-logic-cached',
      'wizard_just_reset',
      'lastPreviewValidationLog',
    ];

    for (const key of sessionOnlyKeys) {
      if (window.localStorage.getItem(key)) {
        window.localStorage.removeItem(key);
        result.removed.push(key);
      }
    }

    if (result.removed.length > 0) {
      /* Intentionally empty */
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    result.errors.push(`Failed to migrate session flags: ${errorMsg}`);
  }

  return result;
}

/**
 * Remove old wizard store data from localStorage
 *
 * Wizard stores should use sessionStorage, not localStorage
 */
export function cleanupOldWizardStores(): CleanupResult {
  const result: CleanupResult = { removed: [], errors: [] };

  if (typeof window === 'undefined') return result;

  try {
    const oldStoreKeys = [
      DEPRECATED_KEYS.OLD_WIZARD_STORE,
      DEPRECATED_KEYS.OLD_MASCOT_STORE,
      DEPRECATED_KEYS.OLD_AI_STORE,
      'wizard-store-session', // Even session-prefixed stores should not be in localStorage
      'mascot-store-session',
      'wizard-ai-store-session',
    ];

    for (const key of oldStoreKeys) {
      if (window.localStorage.getItem(key)) {
        window.localStorage.removeItem(key);
        result.removed.push(key);
      }
    }

    if (result.removed.length > 0) {
      /* Intentionally empty */
    }
  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : String(error);
    result.errors.push(`Failed to cleanup old wizard stores: ${errorMsg}`);
  }

  return result;
}

/**
 * MASTER CLEANUP: Run all cleanup operations
 *
 * Call this once on app initialization to fix over-persistence issues
 */
export function runStorageCleanup(): {
  success: boolean;
  totalRemoved: number;
  errors: string[];
} {
  const results = [
    removeSensitiveDeliveryInfo(),
    consolidateEmailStorage(),
    migrateSessionFlags(),
    cleanupOldWizardStores(),
  ];

  const allRemoved = results.flatMap((r) => r.removed);
  const allErrors = results.flatMap((r) => r.errors);

  if (allRemoved.length > 0) {
    /* Intentionally empty */
  }

  if (allErrors.length > 0) {
    /* Intentionally empty */
  }

  return {
    success: allErrors.length === 0,
    totalRemoved: allRemoved.length,
    errors: allErrors,
  };
}

/**
 * Get current storage usage for debugging
 */
export function getStorageUsage(): {
  localStorage: string[];
  sessionStorage: string[];
} {
  if (typeof window === 'undefined') {
    return { localStorage: [], sessionStorage: [] };
  }

  try {
    const localKeys: string[] = [];
    for (let i = 0; i < window.localStorage.length; i++) {
      const key = window.localStorage.key(i);
      if (key) localKeys.push(key);
    }

    const sessionKeys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i++) {
      const key = window.sessionStorage.key(i);
      if (key) sessionKeys.push(key);
    }

    return {
      localStorage: localKeys.sort(),
      sessionStorage: sessionKeys.sort(),
    };
  } catch (_error) {
    return { localStorage: [], sessionStorage: [] };
  }
}
import { logger } from '@/shared/lib/logger';

/**
 * Debug storage usage (Development utility)
 */
export function debugStorage(): void {
  // Only run in development
  if (import.meta.env.PROD) return;

  if (typeof window === 'undefined') return;

  try {
    const usage = getStorageUsage();
    logger.debug('Storage Debug Info', {
      component: 'storage-cleanup',
      action: 'debug_storage',
      ...usage,
    });
  } catch (_error) {
    // Ignore errors in debug tool
  }
}
